import React, { useCallback, useEffect, useMemo } from "react";
import { useField, useForm } from "react-final-form";
import { CheckCircle, Warning } from "@mui/icons-material";
import { Box, FormControlLabel, Grid, Radio, RadioGroup, TextField as MuiTextField, Typography } from "@mui/material";

import { Accordion } from "ui/Accordion/Accordion";

import { Autocomplete, JsonField, TextField } from "fond/form/fields";
import { InferredIcon } from "fond/map/Legend";
import { Configuration } from "fond/types";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { selectLayersFromConfig } from "fond/utils/configurations";
import { compose, minimum, required, validJSON } from "fond/utils/validation";
import { Dropzone } from "fond/widgets";

import { FormData } from "./SetupForm";

interface ConfigurationStepProps {
  config: Configuration;
}

const ConfigurationStep: React.FC<ConfigurationStepProps> = ({ config }) => {
  const { change } = useForm();

  const {
    meta: { dirty, error: toolConfigError },
    input: { value: toolConfigurations },
  } = useField<FormData["ToolConfigurations"]["CostToServe"]>("ToolConfigurations.CostToServe");

  const {
    meta: { dirty: isOccupancyDirty, error: occupancyError, touched: isOccupancyTouched },
    input: { value: occupancy },
  } = useField<FormData["Inputs"]["Occupancy"]>("Inputs.Occupancy", {
    validate: (value) => {
      const message = "This is a required field";
      if (value.Type === "file" && !value.Data) {
        return { Layer: { Data: message } };
      }

      if (value.Type === "layer" && !value.Layer?.LayerKey) {
        return { Layer: { LayerKey: message } };
      }
      return undefined;
    },
  });

  // Get available layer options from version config
  const layerOptions = useMemo(() => {
    return selectLayersFromConfig(config, ["LAYER"]) as LayerConfig[];
  }, [config]);

  // Set default layer selections. Note that we are making an assumption that the layer to cost
  // could be labeled service_location.
  useEffect(() => {
    if (layerOptions.length > 0 && !dirty) {
      if (!toolConfigurations?.CostingLayer?.LayerKey) {
        const serviceLocation = layerOptions.find((layer) => layer.Key === "service_location");
        if (serviceLocation) {
          change("ToolConfigurations.CostToServe.CostingLayer.LayerKey", serviceLocation.Key);
        }
      }

      if (occupancy.Type === "layer" && !occupancy.Layer?.LayerKey && !isOccupancyDirty) {
        const occupancyLayer = layerOptions.find((layer) => layer.Key === "occupancy");
        if (occupancyLayer) {
          change("Inputs.Occupancy.Layer.LayerKey", occupancyLayer.Key);
        }
      }
    }
  }, [change, toolConfigurations?.CostingLayer?.LayerKey, dirty, layerOptions, occupancy, isOccupancyDirty]);

  const handleOccupancyTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const type = event.target.value as "layer" | "file";
    if (type === "layer") {
      // Reset to default layer
      const occupancyLayer = layerOptions.find((layer) => layer.Key === "occupancy");
      change("Inputs.Occupancy", { Type: "layer", Key: occupancyLayer?.Key ?? null });
    } else {
      change("Inputs.Occupancy", { Type: "file", Data: undefined });
    }
  };

  const handleOccupancyFileChange = useCallback(
    async (newFiles: File[]) => {
      const data = await newFiles[0].text();
      change("Inputs.Occupancy", { Type: "file", Data: data });
    },
    [change]
  );

  const occupancyValue = useMemo(() => {
    if (occupancy?.Type === "layer" && occupancy.Layer?.LayerKey) {
      return layerOptions.find((layer) => layer.Key === occupancy.Layer.LayerKey) || null;
    }
    return null;
  }, [layerOptions, occupancy]);

  const layerToCostValue = useMemo(() => {
    if (toolConfigurations.CostingLayer?.LayerKey) {
      return layerOptions.find((layer) => layer.Key === toolConfigurations.CostingLayer?.LayerKey) || null;
    }
    return null;
  }, [layerOptions, toolConfigurations.CostingLayer?.LayerKey]);

  const renderAutoComplete = useCallback(
    ({ name, label, currentValue }: { name: string; label?: string; currentValue: LayerConfig | null }) => {
      return (
        <Autocomplete
          name={name}
          options={layerOptions}
          label={label}
          placeholder="Select a layer..."
          getOptionLabel={(option) => option?.Label || ""}
          getOptionValue={(option) => option?.Key}
          isOptionEqualToValue={(option, value) => option?.Key === value?.Key}
          renderOption={({ key, ...props }, option) => {
            return (
              <li key={key} {...props}>
                {option.ID && config ? (
                  <Box height="22px">
                    <InferredIcon entity={option} config={config} />
                  </Box>
                ) : null}
                <Typography>{option.Label}</Typography>
              </li>
            );
          }}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: currentValue ? (
                  <Box height="22px">
                    <InferredIcon entity={currentValue} config={config} />
                  </Box>
                ) : null,
              }}
              placeholder="Select a layer..."
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
          size="small"
          validate={required}
        />
      );
    },
    [config, layerOptions]
  );

  return (
    <Grid container spacing={2} mb={1}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Configure Cost to Serve and Design Phase Partitioner
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          A default configuration is already applied. If you need additional settings, you can define them in the JSON editor for Cost to Serve and
          DPP.
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Occupancy
        </Typography>

        <RadioGroup value={occupancy.Type} onChange={handleOccupancyTypeChange} row>
          <FormControlLabel value="layer" control={<Radio />} label="Select from layers" />
          <FormControlLabel value="file" control={<Radio />} label="Upload CSV file" />
        </RadioGroup>

        {occupancy?.Type === "file" && (
          <Box sx={{ mb: 2 }}>
            <Dropzone
              acceptedFiles={{ "text/csv": [".csv"] }}
              message={!occupancy.Data ? "Drag and drop occupancy.csv or click to browse." : undefined}
              data-testid="occupancy-dropzone"
              multiple={false}
              onDropAccepted={handleOccupancyFileChange}
            >
              {occupancy.Type === "file" && occupancy.Data && (
                <Box sx={{ display: "flex", alignItems: "center", p: 1 }}>
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    File successfully uploaded -{" "}
                    <Typography color="primary" component="span">
                      Replace
                    </Typography>
                  </Typography>
                </Box>
              )}
            </Dropzone>
            {occupancyError?.Layer?.Data && isOccupancyTouched && (
              <Typography color="error" component="span">
                * {occupancyError.Layer.Data}
              </Typography>
            )}
          </Box>
        )}

        {occupancy?.Type !== "file" &&
          layerOptions.length > 0 &&
          renderAutoComplete({
            name: "Inputs.Occupancy.Layer.LayerKey",
            currentValue: occupancyValue,
          })}
        {occupancy?.Type !== "file" && layerOptions.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No layers available in version configuration.
          </Typography>
        )}
      </Grid>

      <Grid item xs={12}>
        <Accordion
          label="Cost to serve"
          sx={{ mb: 2, width: "100%" }}
          id="cost-factors"
          defaultExpanded
          startAdornment={toolConfigError ? <Warning color="error" /> : undefined}
        >
          <Grid container sx={{ p: 2 }} spacing={2}>
            <Grid item xs={12} md={6}>
              {layerOptions.length > 0 ? (
                renderAutoComplete({
                  name: "ToolConfigurations.CostToServe.CostingLayer.LayerKey",
                  label: "Layer to Cost",
                  currentValue: layerToCostValue,
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No layers available in version configuration.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="ToolConfigurations.CostToServe.BinCount"
                label="Number of Bins"
                type="number"
                inputProps={{ min: 1 }}
                size="small"
                sx={{ width: 180 }}
                validate={compose(minimum(1), required)}
              />
            </Grid>

            <Grid item xs={12}>
              <JsonField
                name="ToolConfigurations.CostToServe.AdditionalSettings"
                label="Additional Parameters"
                helperText="Enter any JSON parameters"
                validate={validJSON}
              />
            </Grid>
          </Grid>
        </Accordion>
        <Accordion label="DPP" sx={{ width: "100%" }} id="dpp" defaultExpanded>
          <Grid container sx={{ p: 2 }} spacing={2}>
            <Grid item xs={12}>
              <JsonField
                name="ToolConfigurations.DesignPhasePartitioner.AdditionalSettings"
                label="Additional Parameters"
                helperText="Enter any JSON parameters"
                validate={validJSON}
              />
            </Grid>
          </Grid>
        </Accordion>
      </Grid>
    </Grid>
  );
};

export default ConfigurationStep;
